import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AppBar,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Skeleton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../services/dashboardApi';

// ── Tarjeta de KPI ─────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, loading, color }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={48} />
            {subtitle !== undefined && <Skeleton variant="text" width="40%" />}
          </>
        ) : (
          <>
            <Typography
              variant="h4"
              fontWeight="bold"
              color={color || 'text.primary'}
              gutterBottom
            >
              {value ?? '—'}
            </Typography>
            {subtitle !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
  color: PropTypes.string,
};

// ── Página principal ───────────────────────────────────────────────────────────

function DashboardPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const queryClient = useQueryClient();

  const { data: raw, isLoading, error } = useQuery(
    'dashboardStats',
    () => dashboardApi.getStats().then((r) => r.data),
    { staleTime: 4 * 60 * 1000 }   // 4 min — refresca antes de que expire la caché del back
  );

  const stats = raw || {};

  const handleRefresh = () => {
    queryClient.invalidateQueries('dashboardStats');
  };

  // Valores derivados — FDCT matching
  const ratioLabel = stats.gcs_db_ratio != null
    ? stats.gcs_db_ratio.toFixed(4)
    : '—';

  const matchedLabel = stats.itr_fdct_matched != null
    ? `${stats.itr_fdct_matched} / ${stats.itr_fdct_total ?? stats.fdct_total}`
    : '—';

  const percentageLabel = stats.itr_fdct_percentage != null
    ? `${stats.itr_fdct_percentage} %`
    : '—';

  const percentageColor = stats.itr_fdct_percentage != null
    ? (stats.itr_fdct_percentage >= 80
        ? 'success.main'
        : stats.itr_fdct_percentage >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  // Valores derivados — completitud
  const completenessLabel = stats.completeness_complete != null
    ? `${stats.completeness_complete.toLocaleString()} / ${stats.completeness_evaluable?.toLocaleString()}`
    : '—';

  const completenessPctLabel = stats.completeness_pct != null
    ? `${stats.completeness_pct} %`
    : '—';

  const completenessPctColor = stats.completeness_pct != null
    ? (stats.completeness_pct >= 80
        ? 'success.main'
        : stats.completeness_pct >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  const revisionsLabel = stats.total_present_revs != null
    ? `${stats.total_present_revs.toLocaleString()} / ${stats.total_required_revs?.toLocaleString()}`
    : '—';

  const revisionsPctLabel = stats.revisions_pct != null
    ? `${stats.revisions_pct} %`
    : '—';

  const revisionsPctColor = stats.revisions_pct != null
    ? (stats.revisions_pct >= 80
        ? 'success.main'
        : stats.revisions_pct >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  const missingRevsLabel = stats.total_missing_revs != null
    ? stats.total_missing_revs.toLocaleString()
    : '—';

  const definitiveRevLabel = stats.itr_with_definitive_rev != null
    ? `${stats.itr_with_definitive_rev.toLocaleString()} / ${stats.completeness_evaluable?.toLocaleString()}`
    : '—';

  const definitiveRevPctLabel = stats.itr_definitive_rev_pct != null
    ? `${stats.itr_definitive_rev_pct} %`
    : '—';

  const definitiveRevPctColor = stats.itr_definitive_rev_pct != null
    ? (stats.itr_definitive_rev_pct >= 80
        ? 'success.main'
        : stats.itr_definitive_rev_pct >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  const sonoLabel = stats.itr_latest_rev_sono != null
    ? `${stats.itr_latest_rev_sono.toLocaleString()} / ${stats.completeness_evaluable?.toLocaleString()}`
    : '—';

  const sonoPctLabel = stats.itr_latest_rev_sono_pct != null
    ? `${stats.itr_latest_rev_sono_pct} %`
    : '—';

  const sonoPctColor = stats.itr_latest_rev_sono_pct != null
    ? (stats.itr_latest_rev_sono_pct >= 80
        ? 'success.main'
        : stats.itr_latest_rev_sono_pct >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  const anyCriteriaLabel = stats.itr_any_criteria != null
    ? `${stats.itr_any_criteria.toLocaleString()} / ${stats.completeness_evaluable?.toLocaleString()}`
    : '—';

  const anyCriteriaPctLabel = stats.itr_any_criteria_pct != null
    ? `${stats.itr_any_criteria_pct} %`
    : '—';

  const anyCriteriaPctColor = stats.itr_any_criteria_pct != null
    ? (stats.itr_any_criteria_pct >= 80
        ? 'success.main'
        : stats.itr_any_criteria_pct >= 50
          ? 'warning.main'
          : 'error.main')
    : undefined;

  const generatedAt = stats.generated_at
    ? new Date(stats.generated_at).toLocaleString()
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* AppBar */}
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Back to Jobs">
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate('/jobs')}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Progress Dashboard
          </Typography>
          <Tooltip title="Bundle Downloads">
            <IconButton color="inherit" onClick={() => navigate('/bundle')}>
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <span>
              <IconButton color="inherit" onClick={handleRefresh} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton edge="end" color="inherit" onClick={logout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Contenido */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>

        {/* Errores */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading statistics: {error.message}
          </Alert>
        )}
        {stats.gcs_error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>GCS:</strong> {stats.gcs_error}
          </Alert>
        )}
        {stats.fdct_error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>FDCT CSV:</strong> {stats.fdct_error}
          </Alert>
        )}

        {/* KPI cards */}
        <Grid container spacing={3}>

          {/* PDFs totales en GCS */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="PDF files in GCS"
              value={stats.gcs_pdf_files_total?.toLocaleString()}
              subtitle="Total .pdf blobs in the source bucket, before DB filters"
              loading={isLoading}
            />
          </Grid>

          {/* PDFs fuente procesados en BD */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Processed source PDFs in DB"
              value={stats.db_unique_source_files?.toLocaleString()}
              subtitle="Distinct SourcePDF absolute_path entries in the database"
              loading={isLoading}
            />
          </Grid>

          {/* Ratio procesados / total GCS */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Processed / GCS Ratio"
              value={ratioLabel}
              subtitle="Processed DB source paths over total PDFs in the source bucket"
              loading={isLoading}
            />
          </Grid>

          {/* PDFs únicos en GCS por hash */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Unique files in GCS"
              value={stats.gcs_unique_files?.toLocaleString()}
              subtitle="Distinct PDF blobs by MD5 hash in the source bucket"
              loading={isLoading}
            />
          </Grid>

          {/* Total registros ITR en BD */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="ITR records in DB"
              value={stats.itr_db_total?.toLocaleString()}
              subtitle="Total ITRRecord stored in the database"
              loading={isLoading}
            />
          </Grid>

          {/* ITRs del FDCT presentes en BD */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="FDCT ITRs in DB"
              value={matchedLabel}
              subtitle={`FDCT log ITRs with record in DB (total FDCT: ${stats.fdct_total?.toLocaleString() ?? '—'})`}
              loading={isLoading}
            />
          </Grid>

          {/* Porcentaje de cobertura FDCT */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="FDCT Coverage"
              value={percentageLabel}
              subtitle="% of FDCT log ITRs with at least one record in DB"
              loading={isLoading}
              color={percentageColor}
            />
          </Grid>

        </Grid>

        {/* ── Sección: Completitud de revisiones ───────────────────────────── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2, fontWeight: 'bold' }}>
          Revision Completeness
        </Typography>
        <Grid container spacing={3}>

          {/* ITRs completas */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Complete ITRs"
              value={completenessLabel}
              subtitle="ITRs with all revisions present in DB (complete / evaluable)"
              loading={isLoading}
              color={completenessPctColor}
            />
          </Grid>

          {/* % ITRs completas */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Complete ITRs %"
              value={completenessPctLabel}
              subtitle="% of evaluable ITRs with every required revision in DB"
              loading={isLoading}
              color={completenessPctColor}
            />
          </Grid>

          {/* Revisiones hechas / esperadas */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Revisions in DB"
              value={revisionsLabel}
              subtitle="Revisions present in DB vs total expected (done / required)"
              loading={isLoading}
              color={revisionsPctColor}
            />
          </Grid>

          {/* % de progreso de revisiones */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Revisions progress"
              value={revisionsPctLabel}
              subtitle={`${missingRevsLabel} revisions still missing`}
              loading={isLoading}
              color={revisionsPctColor}
            />
          </Grid>

          {/* ITRs con la revisión definitiva del FDCT */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="ITRs with definitive rev"
              value={definitiveRevLabel}
              subtitle="ITRs that have the FDCT Current Revision present in DB"
              loading={isLoading}
              color={definitiveRevPctColor}
            />
          </Grid>

          {/* % ITRs con revisión definitiva */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Definitive rev %"
              value={definitiveRevPctLabel}
              subtitle="% of evaluable ITRs with FDCT Current Revision in DB"
              loading={isLoading}
              color={definitiveRevPctColor}
            />
          </Grid>

          {/* ITRs cuya revisión más reciente en BD tiene estado SONO */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Latest rev SONO"
              value={sonoLabel}
              subtitle="ITRs whose highest revision in DB has status SONO"
              loading={isLoading}
              color={sonoPctColor}
            />
          </Grid>

          {/* % SONO */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Latest rev SONO %"
              value={sonoPctLabel}
              subtitle="% of evaluable ITRs with latest revision in SONO status"
              loading={isLoading}
              color={sonoPctColor}
            />
          </Grid>

          {/* ITRs que cumplen al menos un criterio */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="ITRs any criteria"
              value={anyCriteriaLabel}
              subtitle="ITRs that meet at least one of: all revisions complete, definitive rev present, or latest rev SONO"
              loading={isLoading}
              color={anyCriteriaPctColor}
            />
          </Grid>

          {/* % cualquier criterio */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Any criteria %"
              value={anyCriteriaPctLabel}
              subtitle="% of evaluable ITRs meeting at least one completeness criterion"
              loading={isLoading}
              color={anyCriteriaPctColor}
            />
          </Grid>

        </Grid>

        {/* Timestamp de generación */}
        {generatedAt && !isLoading && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mt: 4, textAlign: 'right' }}
          >
            Generated at: {generatedAt} (cache{' '}
            {Math.floor(((Date.now() - new Date(stats.generated_at)) / 1000) / 60)} min ago)
          </Typography>
        )}

      </Container>
    </Box>
  );
}

export default DashboardPage;
